// Visibility-driven message prefetcher.
// Deduped, debounced, concurrency-capped, LIFO priority queue.

import { sendCommand } from './extension'
import { db } from './db'

const MAX_CONCURRENT = 6
const MAX_CACHED = 1000

// State
const cached = new Set<string>()
const inflight = new Set<string>()
const queue: string[] = [] // LIFO — push to end, pop from end
const queueSet = new Set<string>() // O(1) dedup for queue membership
let drainScheduled = false

/**
 * Process the queue with concurrency limit.
 * Uses setTimeout(0) instead of direct recursion to prevent stack overflow (#9).
 */
function drain() {
  drainScheduled = false

  while (queue.length > 0 && inflight.size < MAX_CONCURRENT) {
    const urn = queue.pop()!
    queueSet.delete(urn)
    if (cached.has(urn) || inflight.has(urn)) continue

    inflight.add(urn)
    fetchOne(urn).finally(() => {
      inflight.delete(urn)
      scheduleDrain() // #9: break recursion with setTimeout
    })
  }
}

function scheduleDrain() {
  if (!drainScheduled && queue.length > 0) {
    drainScheduled = true
    setTimeout(drain, 0)
  }
}

function addToCached(urn: string) {
  // Evict oldest if at capacity (#8)
  if (cached.size >= MAX_CACHED) {
    const first = cached.values().next().value
    if (first) cached.delete(first)
  }
  cached.add(urn)
}

async function fetchOne(conversationUrn: string) {
  try {
    const count = await db.messages
      .where('conversationUrn')
      .equals(conversationUrn)
      .count()

    if (count > 0) {
      addToCached(conversationUrn)
      return
    }

    await sendCommand('FETCH_MESSAGES', { conversationUrn })
    addToCached(conversationUrn)
  } catch {
    // Will retry on next visibility trigger
  }
}

export function ensureMessages(conversationUrn: string): void {
  if (!conversationUrn) return
  if (cached.has(conversationUrn) || inflight.has(conversationUrn)) return
  if (queueSet.has(conversationUrn)) return

  queue.push(conversationUrn)
  queueSet.add(conversationUrn)
  scheduleDrain()
}

let debounceTimer: ReturnType<typeof setTimeout> | undefined

export function ensureMessagesVisible(urns: string[]): void {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    queue.length = 0
    queueSet.clear()
    for (const urn of urns) {
      if (urn && !cached.has(urn) && !inflight.has(urn) && !queueSet.has(urn)) {
        queue.push(urn)
        queueSet.add(urn)
      }
    }
    scheduleDrain()
  }, 50)
}

export function prefetchAdjacent(
  conversations: { conversationUrn: string }[],
  currentUrn: string
): void {
  const idx = conversations.findIndex((c) => c.conversationUrn === currentUrn)
  if (idx === -1) return

  if (idx > 0) ensureMessages(conversations[idx - 1].conversationUrn)
  if (idx < conversations.length - 1) ensureMessages(conversations[idx + 1].conversationUrn)
}

export function invalidateMessages(conversationUrn: string): void {
  cached.delete(conversationUrn)
}
